import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Grid2'
import CaleoInputLabel from 'Components/reusable/CaleoCustomComponents/CaleoInputLabel'
import React, { ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'
import { InputField } from '../InputField'

interface OwnProps {
  className?: string
  error?: string | false
  fullWidth?: boolean
  grow?: boolean
  helperText?: string | null
  label?: string
  required?: boolean
  style?: React.CSSProperties
  inputType?: string
  setInputType?: (type: string) => void
  expandedInputOptions?: boolean
  insetLabel?: boolean
  ariaLabel?: string
}

/**
 * Experience time picker component.
 *
 * @param className - {string} Class name for styling the component.
 * @param error - {string | false} Error message.
 * @param fullWidth - {boolean} Should component be full width.
 * @param grow - {boolean} Should component grow.
 * @param helperText - {string | null} Helper text.
 * @param label - {string} Label text.
 * @param required - {boolean} Is the component required.
 * @param style - {React.CSSProperties} Style parameter of the component.
 * @param inputType - {string} Input type.
 * @param setInputType - Function to set input type.
 * @param expandedInputOptions - {boolean} Should the expanded options be displayed.
 * @returns the experience time picker component.
 */
export const ExperienceTimePicker: React.FC<OwnProps & Omit<ComponentProps<typeof TextField>, keyof OwnProps>> = ({
  className,
  error,
  fullWidth,
  grow,
  helperText,
  label,
  required,
  style: styleProp = {},
  inputType,
  setInputType,
  expandedInputOptions = false,
  insetLabel,
  ariaLabel,
  ...rest
}) => {
  const { t } = useTranslation()
  const labelId = React.useId()
  const resolvedAriaLabel = ariaLabel ?? t('profile.skill.modal.experience.inputType')

  const style = {
    ...styleProp,
    width: fullWidth ? '100%' : undefined,
    flexGrow: grow ? 1 : undefined,
  }

  return (
    <Grid style={style}>
      {!insetLabel && <CaleoInputLabel id={labelId} required={required} label={label} />}
      {inputType && setInputType && (
        <FormControl component="fieldset">
          <RadioGroup
            {...(!insetLabel && label ? { 'aria-labelledby': labelId } : { 'aria-label': resolvedAriaLabel })}
            name="experienceInputType"
            value={inputType}
            onChange={e => setInputType(e.target.value)}
          >
            <Grid container direction="row">
              <FormControlLabel value="years" control={<Radio />} label={t('profile.skill.modal.experience.years')} />
              <FormControlLabel value="months" control={<Radio />} label={t('profile.skill.modal.experience.months')} />
              {expandedInputOptions && (
                <>
                  <FormControlLabel value="days" control={<Radio />} label={t('profile.skill.modal.experience.days')} />
                  <FormControlLabel
                    value="manDays"
                    control={<Radio />}
                    label={t('profile.skill.modal.experience.manDays')}
                  />
                  <FormControlLabel
                    value="manMonths"
                    control={<Radio />}
                    label={t('profile.skill.modal.experience.manMonths')}
                  />
                </>
              )}
            </Grid>
          </RadioGroup>
        </FormControl>
      )}
      <InputField
        margin="dense"
        slotProps={{
          formHelperText: { style: { textAlign: 'right' } },
        }}
        error={error}
        className={`${className}`}
        label={insetLabel ? label : undefined}
        ariaLabel={insetLabel ? ariaLabel : undefined}
        ariaLabelledBy={!insetLabel && label ? labelId : undefined}
        variant="outlined"
        helperText={error ? error : helperText}
        insetLabel={insetLabel}
        {...{ ...rest, fullWidth }}
      />
    </Grid>
  )
}
